import React, { useState, useEffect, useContext, FC } from 'react'
import Web3 from 'web3'

import {
  supportedChains,
  defaultChainID,
  findTokenDefinition,
  TokenDefinition,
  WhitelistedPool,
  getNetworkName,
} from '../config'

import Web3Modal from '@nicorama/web3modal'

import WalletConnectProvider from '@walletconnect/web3-provider'
import {
  Account,
  Address,
  ChainConfiguration,
  InjectedWindow,
  OptionalAccount,
} from '../utils/types'
import {
  lookIfConnected,
  isCypressInjected,
  isMetamaskInjected,
  isProviderUnlocked,
  getProviderType,
} from '../components/connection/provider'
import {
  isDocumentVisible,
  lastClickOnWallet,
} from '../utils/document-visibility'
import { now } from '../utils/dates'
import { createWalletEvent, identify } from 'user-events-segment'
declare const window: InjectedWindow

export function getWeb3() {
  if (Web3.givenProvider) {
    return [true, new Web3(Web3.givenProvider)]
  }
  //	const ws = new Web3.providers.WebsocketProvider(`wss://kovan.infura.io/ws/v3/${process.env.REACT_APP_INFURA_API_KEY}`)
  //	if (ws) {
  //		return [false, new Web3(ws)]
  //	}
  return [false, undefined]
}

// TODO: what is the purpose of this function ?
export function requestAccount(isConnectedToWeb3: boolean) {
  //	if (window.ethereum) {
  //		return window.ethereum
  //			.request({ method: "eth_requestAccounts" })
  //	}
  return Promise.reject(undefined)
}
/* A context tends to augment a mappingConfiguration by replacing addresses with useful objects */
// TODO: should differentiate a Web3 and ConnectedWeb3 interfaces
export interface Web3ContextInterface {
  allowWeb3: boolean
  provider: WalletConnectProvider | undefined
  isConnectedToWeb3: boolean
  web3: Web3 | undefined
  appChainID: number
  account: OptionalAccount
  factoryContractAddress: Address
  proxy: Address
  poolContractAddress: Address
  legacyPoolContract: Address | undefined
  // TODO: not good
  whitelist: TokenDefinition[]
  whitelistPools: WhitelistedPool[]
  defaultToken: TokenDefinition | undefined
  fromBlock: number
  hasFaucet: boolean
  maxLookbackPeriodInBlock: number
  switchNetwork: (intChainId: number, config: ChainConfiguration) => void
  setAllowWeb3: (allow: boolean) => void
  disconnect: () => Promise<void>
  config: ChainConfiguration
  currentBlockTimestamp: number | undefined
  currentBlockNumber: number | undefined
}

const defaultChain = supportedChains[0]

const defaultContext: Web3ContextInterface = {
  allowWeb3: !!localStorage.getItem('WEB3_CONNECT_CACHED_PROVIDER'),
  provider: undefined,
  isConnectedToWeb3: false,
  web3: isCypressInjected() ? window.ethereum : undefined,
  appChainID: defaultChainID,
  account: undefined,
  switchNetwork: (x, y) => {},
  setAllowWeb3: (allow: boolean) => {},
  currentBlockTimestamp: undefined,
  ...defaultChain,
  disconnect: async () => {},
  defaultToken: findTokenDefinition(defaultChain, defaultChain.defaultToken)!,
  currentBlockNumber: undefined,
}

export const Web3Context =
  React.createContext<Web3ContextInterface>(defaultContext)

export const Web3Provider: FC = ({ children }) => {
  const [allowWeb3, setAllowWeb3] = useState<boolean>(false)

  const [provider, setProvider] = useState<WalletConnectProvider>()

  const [isConnectedToWeb3, setIsConnectedToWeb3] = useState(
    defaultContext.isConnectedToWeb3
  )
  const [web3, setWeb3] = useState<Web3 | undefined>(
    isCypressInjected() ? window.ethereum : undefined
  )

  const [appChainID, setAppChainID] = useState(defaultContext.appChainID)
  const [account, setAccount] = useState<OptionalAccount>(
    defaultContext.account
  )

  const [factoryContractAddress, setFactoryContractAddress] = useState(
    defaultContext.factoryContractAddress
  )
  const [proxy, setProxy] = useState(defaultContext.proxy)
  const [poolContractAddress, setPoolContractAddress] = useState(
    defaultContext.poolContractAddress
  ) // TODO: remove this + dependencies
  const [legacyPoolContract, setLegacyPoolContract] = useState(
    defaultContext.legacyPoolContract
  )
  const [whitelist, setWhitelist] = useState(defaultContext.whitelist)
  const [whitelistPools, setWhitelistPools] = useState(
    defaultContext.whitelistPools
  )
  const [defaultToken, setDefaultToken] = useState<TokenDefinition>()
  const [fromBlock, setFromBlock] = useState(defaultContext.fromBlock)
  const [hasFaucet, setHasFaucet] = useState(defaultContext.hasFaucet)
  const [maxLookbackPeriodInBlock, setMaxLookbackPeriodInBlock] = useState(
    defaultContext.maxLookbackPeriodInBlock
  )
  const [chainSupported, setChainSupported] = useState(false)
  const [currentBlockNumber, setCurrentBlockNumber] = useState<
    number | undefined
  >(undefined)
  const [currentBlockTimestamp, setCurrentBlockTimestamp] = useState<
    number | undefined
  >(undefined)

  const providerOptions: any = {
    injected: {
      display: {
        name: 'Browser',
        description: 'Connect to the injected provider (window.ethereum)',
      },
      package: null,
    },
    walletconnect: {
      package: WalletConnectProvider,
      options: {
        rpc: {
          137: `https://polygon-mainnet.infura.io/v3/${process.env.REACT_APP_INFURA_API_KEY}`,
        },
      },
    },
  }

  const web3Modal = new Web3Modal({
    //	  network: "mainnet", // optional
    cacheProvider: true, // optional
    providerOptions, // required
    disableInjectedProvider: false,
  })

  // Check if Metamask has already been connected on an account
  // Also will try to force connection only if Metamask is not locked
  useEffect(() => {
    lookIfConnected().then(connected => {
      if (connected) {
        connectWithWeb3Modal()
        setAllowWeb3(true)
      }
    })
  }, [])

  /**
   *Use effect for Cypress, at the start of the app
   */
  useEffect(() => {
    if (isCypressInjected()) {
      //setAllowWeb3(true)
      setAppChainID(defaultChainID)
      const _web3 = window.ethereum
      setWeb3(_web3)
      setIsConnectedToWeb3(true)
      _web3.eth.getAccounts().then((accounts: string[]) => {
        if (accounts != undefined && accounts.length > 0) {
          setAccount(_web3.eth.defaultAccount)
          setIsConnectedToWeb3(true)
        } else {
          throw 'no cypress account'
        }
      })
    }
  }, [])

  useEffect(() => {
    if (allowWeb3) {
      if (!isDocumentVisible()) {
        // document is not visible, avoiding metamask popup
        setAllowWeb3(false)
        return
      }
      // TODO : remove lastClickOnWallet
      /*
      if (!isAlreadyConnected() && now() - lastClickOnWallet.time > 500) {
        // too much lag, will not popup
        setAllowWeb3(false)
        return
      }*/
      lookIfConnected().then(connected => {
        if (!connected) {
          connectWithWeb3Modal()
        }
      })
    }
  }, [allowWeb3])

  useEffect(() => {
    const mapping = supportedChains.find(m => m.config.appId === appChainID)
    if (mapping) {
      setChainSupported(true)
      setFactoryContractAddress(mapping.factoryContractAddress)
      setProxy(mapping.proxy)
      setPoolContractAddress(mapping.poolContractAddress)
      if (mapping.legacyPoolContract) {
        setLegacyPoolContract(mapping.legacyPoolContract)
      }
      setWhitelist(mapping.whitelist)
      setWhitelistPools(mapping.whitelistPools)
      setDefaultToken(findTokenDefinition(mapping, mapping.defaultToken))
      setFromBlock(mapping.fromBlock)
      setHasFaucet(mapping.hasFaucet)
      setMaxLookbackPeriodInBlock(mapping.maxLookbackPeriodInBlock)
    } else {
      setChainSupported(false)
    }
  }, [appChainID])

  function connectWithWeb3Modal() {
    if (isCypressInjected()) {
      // no need for web3Modal
      return
    }
    web3Modal
      .connect()
      .then(_provider => {
        if (_provider) {
          // Subscribe to accounts change
          _provider.on('accountsChanged', (accounts: Account[]) => {
            if (accounts != undefined && accounts.length > 0) {
              setAccount(accounts[0])
            } else {
              resetAll()
            }
          })

          // Subscribe to chainId change
          _provider.on('chainChanged', (chainId: number) => {
            if (chainId != undefined) {
              handleChainId(chainId)
            } else {
              setAppChainID(defaultChainID)
            }
          })

          // Subscribe to provider connection
          _provider.on('connect', (info: { chainId: number }) => {
            if (info && info.chainId != undefined) {
              handleChainId(info.chainId)
            } else {
              resetAll()
            }
          })

          // Subscribe to provider disconnection
          //					_provider.on("disconnect", (error: { code: number; message: string }) => {
          //						if (error != undefined && error.code != undefined) {
          //							console.log("disconnect")
          //							resetAll()
          //						}
          //					});

          const _web3 = new Web3(_provider)

          setProvider(_provider)
          setWeb3(_web3)

          _web3.eth.getAccounts().then(accounts => {
            if (accounts !== undefined && accounts.length > 0) {
              const walletAddress = accounts[0]
              setAccount(walletAddress)
              setIsConnectedToWeb3(true)
              identify(walletAddress)
              window.applicationContext.selectedProvider = getProviderType()
            }
          })

          _web3.eth.getChainId().then(chainID => {
            setAppChainID(chainID)
            createWalletEvent().wallet_connected(
              getProviderType(),
              getNetworkName(chainID)
            )
          })
        } else {
          resetAll()
        }
      })
      .catch(error => {
        console.error(error)
        resetAll()
      })
  }

  function handleChainId(chainId: number) {
    setAppChainID(parseInt(chainId.toString(), 16))
  }

  /**
   * The connect/disconnect functionality is entirely in the user's hands due to security and privacy concerns.
   * You can only pretend a disconnect by resetting a provider, chainId and selectedAccount to null
   * and clearing the cache of the provided you previously used.
   * (the way PancakeSwap does, Uniswap too,)
   * https://ethereum.stackexchange.com/a/111390/87100
   */
  async function resetAll() {
    // TODO: no catch
    await web3Modal.clearCachedProvider()
    if (provider != undefined && provider.close) {
      try {
        await provider.close()
      } catch {}
      setProvider(undefined)
    }
    setAllowWeb3(false)
    setIsConnectedToWeb3(false)
    //     console.log('#### setting Web3 to undefined !!')
    setWeb3(undefined)
    setAppChainID(defaultChainID)
    setAccount(undefined)
    setCurrentBlockNumber(undefined)
    setCurrentBlockTimestamp(undefined)
    if (window.ethereum) {
      try {
        window.ethereum.selectedAddress = undefined
      } catch (e) {
        // This is due to window.ethereum.selectedAddress having only
        // a getter and no setter. We can ignore this error.
      }
    }
  }

  async function disconnect() {
    createWalletEvent().clicked_disconnect_wallet(
      getProviderType(),
      getNetworkName(appChainID)
    )
    return resetAll()
  }

  async function switchNetwork(intChainId: number, config: ChainConfiguration) {
    const hexChainId = `${'0x' + intChainId.toString(16)}`
    if (!provider) {
      throw "no provider found, can't change network"
    }

    try {
      if (provider.request) {
        await provider.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: hexChainId }],
        })
      }
    } catch (switchError: any) {
      // This error code indicates that the chain has not been added to MetaMask.
      if (switchError.code === 4902) {
        try {
          await provider.request({
            method: 'wallet_addEthereumChain',
            params: [
              {
                chainId: `${hexChainId}`, // A 0x-prefixed hexadecimal string
                chainName: `${config.network} ${config.chain}`,
                nativeCurrency: config.nativeCurrency,
                rpcUrls: config.rpcUrls,
                blockExplorerUrls: config.blockExplorerUrls,
              },
            ],
          })
        } catch (e) {
          throw 'Unsupported exception ' + e
        }
      }
    }
  }

  return (
    <Web3Context.Provider
      value={{
        allowWeb3,
        setAllowWeb3,
        isConnectedToWeb3,
        web3,
        appChainID,
        account,
        factoryContractAddress,
        proxy,
        poolContractAddress,
        whitelist,
        defaultToken,
        fromBlock,
        hasFaucet,
        provider,
        switchNetwork,
        disconnect,
        maxLookbackPeriodInBlock,
        legacyPoolContract,
        config: defaultContext.config,
        whitelistPools,
        currentBlockTimestamp,
        currentBlockNumber,
      }}
    >
      {children}
    </Web3Context.Provider>
  )
}
